import {
  Dispatch,
  Key,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Alert } from "antd";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

import VLTable, { AvailableFilter } from "views/uikit/VLTable";

import useFilterOptions from "./useFilterOptions";
import useScanResults from "./useScanResults";
import useScanResultsData, { ScanResultRow } from "./useScanResultsData";
import WaferTable from "./WaferTable";
import { incompatibilityReason, TargetDataset } from "./targetDataset";
import { ScanExportStatus, ScanResultsFilters } from "./types";
import { PAGE_PARAMETER } from "helpers/constants/miscellaneous";
import styles from "./style.module.scss";

const API_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss";

// No default: the backend leaves an omitted bound open, and on this index a one-year
// default hid 65 of 70 scan results.
const SCAN_TIME_OPTIONS = [
  { text: "Last 24 Hours", value: "1" },
  { text: "Last 7 Days", value: "7" },
  { text: "Last 30 Days", value: "30" },
  { text: "Last 90 Days", value: "90" },
  { text: "Last Year", value: "365" },
];

const toOptions = (values: string[]) =>
  values.map((value) => ({ text: value, value }));

type FilterValues = Record<string, string[] | null>;

/** Wafer paths picked per scan result, keyed by the scan result's path. */
export type SelectedWafers = Record<string, string[]>;

interface ScanResultsBrowserProps {
  selectedWafers: SelectedWafers;
  onSelectedWafersChange: Dispatch<SetStateAction<SelectedWafers>>;
  /** Export state per scan result path; queued, exporting and added rows are locked. */
  exportStatus?: Record<string, ScanExportStatus>;
  /** Display name per scan result path, for callers listing what they exported. */
  onNamesChange?: (names: Record<string, string>) => void;
  /**
   * The existing dataset these scan results would be added to. Its Job and Setup pin the
   * search, and scan results it would refuse are greyed out and cannot be picked.
   */
  target?: TargetDataset | null;
  /** Hold the search until `target` has loaded, so it starts out narrowed. */
  targetPending?: boolean;
}

const EMPTY_INCOMPATIBLE = new Map<string, string>();

// The status itself lives in the folder table below; here the row only locks, and the
// tooltip says why.
const LOCKED_TOOLTIP: Partial<Record<ScanExportStatus, string>> = {
  queued: "Already added - waiting to be exported",
  exporting: "Already added - being exported",
  added: "Already added to this dataset",
};

const EMPTY_STATUS: Record<string, ScanExportStatus> = {};

/**
 * Filters and grid of the scan results DataServer knows about, with wafer-level
 * selection.
 *
 * Selection is owned by the caller, since what happens to it - the standalone page's
 * Continue, or the creation wizard's - lives outside this component. Assumes DataServer
 * is available; gating on that is the caller's job.
 */
export default function ScanResultsBrowser({
  selectedWafers,
  onSelectedWafersChange: setSelectedWafers,
  exportStatus = EMPTY_STATUS,
  onNamesChange,
  target = null,
  targetPending = false,
}: ScanResultsBrowserProps) {
  // The filter pills above the table. Server-side: they narrow what DataServer returns,
  // unlike the column header filters below, which only narrow the rows already loaded.
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [filterState, setFilterState] = useState<Record<string, string[]>>({});
  const [searchParams, setSearchParams] = useSearchParams();

  // VLTable keeps the page number in the URL and never resets it, so narrowing the
  // result set while sitting on a later page would show a blank table.
  const resetToFirstPage = useCallback(() => {
    if (!searchParams.get(PAGE_PARAMETER)) return;
    const next = new URLSearchParams(searchParams);
    next.set(PAGE_PARAMETER, "1");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const appliedFilters: ScanResultsFilters = useMemo(() => {
    const scanTime = filterValues.scanTime ?? [];
    // Two values are a custom calendar range, already in API format; one is a preset
    // window in days, open-ended towards now.
    const [from, to] =
      scanTime.length === 2
        ? scanTime
        : scanTime.length === 1
          ? [
              dayjs()
                .subtract(Number(scanTime[0]), "day")
                .format(API_DATE_FORMAT),
              undefined,
            ]
          : [undefined, undefined];
    return {
      job: filterValues.job?.[0],
      setup: filterValues.setup?.[0],
      lot: filterValues.lot?.[0],
      from,
      to,
    };
  }, [filterValues]);

  // Adding to a dataset: Job and Setup are pinned to the dataset's own. Derived rather
  // than copied into the filter state, so the very first search is already the narrowed
  // one.
  const searchFilters: ScanResultsFilters = useMemo(
    () =>
      target
        ? {
            ...appliedFilters,
            job: target.job ?? appliedFilters.job,
            setup: target.setup ?? appliedFilters.setup,
          }
        : appliedFilters,
    [appliedFilters, target],
  );

  const { scanResults, truncated, isLoading, error } = useScanResults(
    searchFilters,
    !targetPending,
  );

  // Scan results the target dataset would refuse at select_folders. The search filter is
  // a LIKE match, so a neighbouring Job or Setup can still come back and lands here too.
  const incompatible = useMemo(() => {
    if (!target) return EMPTY_INCOMPATIBLE;
    return new Map(
      scanResults.flatMap((result) => {
        const reason = incompatibilityReason(result, target);
        return reason ? [[result.sr_path, reason] as [string, string]] : [];
      }),
    );
  }, [scanResults, target]);
  const incompatiblePaths = useMemo(
    () => Array.from(incompatible.keys()),
    [incompatible],
  );
  const { columns, dataSource, wafersByPath } = useScanResultsData(
    scanResults,
    filterState,
    selectedWafers,
  );

  useEffect(() => {
    onNamesChange?.(
      Object.fromEntries(dataSource.map((row) => [row.srPath, row.srName])),
    );
  }, [dataSource, onNamesChange]);

  // A scan result already on its way into the dataset cannot be picked again - that
  // would export it twice. Failed ones stay pickable, so they can be re-added.
  const lockedPaths = useMemo(
    () =>
      Object.keys(exportStatus).filter(
        (path) => LOCKED_TOOLTIP[exportStatus[path]],
      ),
    [exportStatus],
  );
  const lockedTooltips = useMemo(
    () =>
      Object.fromEntries(
        lockedPaths.map((path) => [path, LOCKED_TOOLTIP[exportStatus[path]]!]),
      ),
    [exportStatus, lockedPaths],
  );

  // Everything that cannot be picked, and why: on its way in already, or refused by the
  // target dataset.
  const unpickablePaths = useMemo(
    () => [...lockedPaths, ...incompatiblePaths],
    [lockedPaths, incompatiblePaths],
  );
  const unpickableTooltips = useMemo(
    () => ({ ...lockedTooltips, ...Object.fromEntries(incompatible) }),
    [lockedTooltips, incompatible],
  );

  // The lot rows the grid shows are a display grouping whose own path DataServer will not
  // export, so the parent checkboxes are derived from the wafer selection rather than
  // held alongside it - two stores would be two chances to send the wrong level again.
  // A lot row reads as selected when any of its wafers is. antd has no indeterminate
  // state to drive from here, so the Wafers column shows "2 / 5" for a partial pick.
  const selectedRowKeys: Key[] = useMemo(
    () => Object.keys(selectedWafers),
    [selectedWafers],
  );

  // Ticking a lot takes all of its wafers; clearing it drops the entry entirely, which
  // is what keeps Object.keys above an accurate list of what is selected.
  const handleRowSelectionChange = useCallback(
    (keys: Key[]) => {
      const wanted = new Set(
        keys.map(String).filter((key) => !incompatible.has(key)),
      );
      setSelectedWafers((current) => {
        const next: SelectedWafers = {};
        wanted.forEach((srPath) => {
          next[srPath] =
            current[srPath] ??
            (wafersByPath[srPath] ?? []).map((wafer) => wafer.path);
        });
        return next;
      });
    },
    [wafersByPath, setSelectedWafers, incompatible],
  );

  const handleWaferSelectionChange = useCallback(
    (srPath: string, paths: string[]) => {
      setSelectedWafers((current) => {
        const next = { ...current };
        if (paths.length) next[srPath] = paths;
        else delete next[srPath];
        return next;
      });
    },
    [setSelectedWafers],
  );
  const filterOptions = useFilterOptions();

  const availableFilters: AvailableFilter[] = useMemo(
    () => [
      {
        key: "scanTime",
        title: "Scan Time",
        options: SCAN_TIME_OPTIONS,
        type: "dateRange",
      },
      // A Job or Setup the target dataset pins is not the user's to change.
      ...(target?.job
        ? []
        : [
            {
              key: "job",
              title: "Job",
              options: toOptions(filterOptions.jobs),
              searchable: true,
              type: "single" as const,
            },
          ]),
      ...(target?.setup
        ? []
        : [
            {
              key: "setup",
              title: "Setup",
              options: toOptions(filterOptions.setups),
              searchable: true,
              type: "single" as const,
            },
          ]),
      {
        key: "lot",
        title: "Lot",
        options: toOptions(filterOptions.lots),
        searchable: true,
        type: "single",
      },
    ],
    [filterOptions, target],
  );

  const handleFilterValuesChange = (values: FilterValues) => {
    resetToFirstPage();
    setFilterValues(values);
  };

  const visibleRows = useMemo(() => {
    const activeKeys = Object.keys(filterState).filter(
      (key) => filterState[key]?.length,
    );
    if (!activeKeys.length) return dataSource;
    return dataSource.filter((row) =>
      activeKeys.every((key) => filterState[key].includes(String(row[key]))),
    );
  }, [dataSource, filterState]);

  const handleFilterChange = (filters: Record<string, string[] | null>) => {
    resetToFirstPage();
    setFilterState(
      Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [key, value ?? []]),
      ),
    );
  };

  return (
    <div className={styles.body}>
      {error && (
        <Alert type="error" showIcon message={error} className={styles.alert} />
      )}

      {target && (target.job || target.setup || target.recipes) && (
        <Alert
          type="info"
          showIcon
          className={styles.alert}
          message={
            `Only scan results matching ${target.name} can be added: ` +
            [
              target.job && `Job ${target.job}`,
              target.setup && `Setup ${target.setup}`,
              target.recipes && `Recipes ${target.recipes.join(", ")}`,
            ]
              .filter(Boolean)
              .join(", ") +
            ". Others are greyed out."
          }
        />
      )}

      {truncated && !error && (
        <Alert
          type="info"
          showIcon
          className={styles.alert}
          message="More wafer scans match this time range than could be read, so some scan results may be missing wafers and their counts understated. Narrow the dates or filters for exact numbers."
        />
      )}

      <div className={styles.table}>
        <VLTable<ScanResultRow>
          columns={columns}
          dataSource={visibleRows}
          loading={isLoading}
          onTableFilterChange={handleFilterChange}
          tableFilters={{
            availableFilters,
            filters: filterValues,
            onTableFilterChange: handleFilterValuesChange,
            dropdownClassName: styles.raisedDropdown,
          }}
          localeEmptyText="No scan results for these filters"
          rowKey="key"
          // Columns are percentages plus one flexible column; fixed layout makes them
          // add up to the container, and long values ellipsize rather than widen it.
          tableLayout="fixed"
          // Task Manager's density. The shared stylesheet leaves body-cell
          // padding to the theme, so this prop is the whole difference.
          size="middle"
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleRowSelectionChange}
          disabledRowKeys={incompatiblePaths}
          checkboxDisabledKeys={unpickablePaths}
          checkboxTooltipData={unpickableTooltips}
          // Wafers are what DataServer exports, so they have to be reachable.
          // VLTable supplies the caret itself - passing expandIcon here would be
          // overridden.
          expandable={{
            expandedRowRender: (record) => (
              <WaferTable
                wafers={wafersByPath[record.srPath] ?? []}
                selectedPaths={selectedWafers[record.srPath] ?? []}
                onChange={(paths) =>
                  handleWaferSelectionChange(record.srPath, paths)
                }
                disabled={lockedPaths.includes(record.srPath)}
              />
            ),
            // A refused scan result must not be reachable through its wafers.
            rowExpandable: (record) =>
              !incompatible.has(record.srPath) &&
              (wafersByPath[record.srPath] ?? []).length > 0,
          }}
          // Paging keeps this a fixed-size table. It also matters structurally:
          // VLTable drops its scroll-height cap when paginated, and that cap was
          // what let the table ratchet taller as rows arrived.
          pagination={{
            total: visibleRows.length,
            tooltip: "",
            // Task Manager's page sizes. Taller rows mean 20 no longer fits a
            // desktop viewport; the table scrolls within its flex box on short
            // ones either way.
            desktopItemsLength: 18,
            laptopItemsLength: 10,
          }}
        />
      </div>
    </div>
  );
}

/** "3 wafers in 2 scan results selected", or empty when nothing is. */
export function describeSelection(selectedWafers: SelectedWafers): string {
  const scanResultCount = Object.keys(selectedWafers).length;
  const waferCount = Object.values(selectedWafers).reduce(
    (total, paths) => total + paths.length,
    0,
  );
  if (!waferCount) return "";
  return (
    `${waferCount} wafer${waferCount === 1 ? "" : "s"} ` +
    `in ${scanResultCount} scan result${scanResultCount === 1 ? "" : "s"} selected`
  );
}
